import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useSeo from '../hooks/useSeo';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../components/ui/accordion';
import {
  BookOpen, Target, ListChecks, Trophy, ArrowLeft, Lock,
  Cog, Map, Users, Compass, Sparkles, CheckCircle2, Clock, Circle,
  Save, RotateCcw, GraduationCap,
} from 'lucide-react';
import { META_LIVRET, COMPETENCES_LIVRET } from '../data/livretApprentissageA';
import api from '../api';
import { toast } from 'sonner';
import LivretImportDialog from '../components/student/LivretImportDialog';
import { decodeImportPayload } from '../components/teacher/ShareValidationsDialog';

const COMP_ICONS = { C1: Cog, C2: Map, C3: Users, C4: Compass };
const COMP_COLORS = { C1: '#0891b2', C2: '#7c3aed', C3: '#dc2626', C4: '#16a34a' };

const STATUS_META = {
  non_acquis: {
    label: 'Non acquis',
    short: 'À travailler',
    color: '#64748b',
    bg: 'rgba(100,116,139,0.12)',
    icon: Circle,
  },
  en_cours: {
    label: 'En cours',
    short: 'En cours',
    color: '#d97706',
    bg: 'rgba(217,119,6,0.15)',
    icon: Clock,
  },
  acquis: {
    label: 'Acquis',
    short: 'Acquis',
    color: '#16a34a',
    bg: 'rgba(22,163,74,0.15)',
    icon: CheckCircle2,
  },
};

const STATUS_ORDER = ['non_acquis', 'en_cours', 'acquis'];

function StatusButton({ value, current, onClick, disabled }) {
  const meta = STATUS_META[value];
  const Icon = meta.icon;
  const active = current === value;
  return (
    <Button
      type="button"
      variant={active ? 'default' : 'outline'}
      size="sm"
      onClick={() => onClick(value)}
      disabled={disabled}
      data-testid={`livret-status-btn-${value}`}
      className="h-9 gap-1.5 transition-all"
      style={active
        ? { backgroundColor: meta.color, color: '#fff', borderColor: meta.color }
        : { borderColor: meta.color, color: meta.color, background: 'transparent' }}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="text-xs font-semibold">{meta.short}</span>
    </Button>
  );
}

function SubCompetencyCard({ sc, item, onUpdate, disabled }) {
  const status = item?.status || 'non_acquis';
  const initialNotes = item?.notes || '';
  const [localNotes, setLocalNotes] = useState(initialNotes);
  const [savingNotes, setSavingNotes] = useState(false);
  const debounceRef = useRef(null);
  const lastSyncedNotesRef = useRef(initialNotes);

  // Sync local notes when server data changes externally (ex : import depuis lien).
  // On compare via ref pour éviter le pattern setState-in-effect inutile.
  if (initialNotes !== lastSyncedNotesRef.current) {
    lastSyncedNotesRef.current = initialNotes;
    // setState pendant le rendu → React l'applique avant l'engagement suivant.
    setLocalNotes(initialNotes);
  }

  const handleStatusClick = (newStatus) => {
    if (newStatus === status) return;
    onUpdate(sc.code, { status: newStatus });
  };

  const handleNotesChange = (e) => {
    const v = e.target.value.slice(0, 2000);
    setLocalNotes(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSavingNotes(true);
      try {
        await onUpdate(sc.code, { notes: v }, { silent: true });
      } finally {
        setSavingNotes(false);
      }
    }, 800);
  };

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const meta = STATUS_META[status];

  return (
    <AccordionItem value={sc.code} className="border rounded-lg overflow-hidden">
      <AccordionTrigger
        className="px-4 py-3 hover:no-underline hover:bg-accent/40 transition-colors"
        data-testid={`livret-item-trigger-${sc.code}`}
      >
        <div className="flex items-center gap-3 w-full text-left">
          <Badge
            variant="outline"
            className="font-mono text-[10px] flex-shrink-0"
            style={{ borderColor: meta.color, color: meta.color, background: meta.bg }}
          >
            {sc.code}
          </Badge>
          <span className="flex-1 text-sm md:text-base font-medium">{sc.intitule}</span>
          <Badge
            className="ml-auto text-[10px] flex-shrink-0 whitespace-nowrap"
            style={{ backgroundColor: meta.color, color: '#fff', border: 'none' }}
          >
            {meta.short}
          </Badge>
        </div>
      </AccordionTrigger>

      <AccordionContent className="px-4 pb-4 pt-1 space-y-4">
        {/* Objectif */}
        <div className="rounded-md border border-cyan-700/30 bg-cyan-900/10 p-3">
          <div className="flex items-center gap-2 mb-1.5">
            <Target className="w-4 h-4 text-cyan-400" />
            <h4 className="text-xs font-bold uppercase tracking-wide text-cyan-400">
              Ce que tu dois savoir faire
            </h4>
          </div>
          <p className="text-sm text-foreground/90 leading-relaxed">{sc.objectif}</p>
        </div>

        {/* Critères de réalisation */}
        <div className="rounded-md border border-sky-700/30 bg-sky-900/10 p-3">
          <div className="flex items-center gap-2 mb-2">
            <ListChecks className="w-4 h-4 text-sky-400" />
            <h4 className="text-xs font-bold uppercase tracking-wide text-sky-400">
              Comment savoir si c&apos;est acquis ?
            </h4>
          </div>
          <ul className="space-y-1.5">
            {sc.criteres.map((c, i) => (
              <li key={i} className="text-sm text-foreground/85 flex items-start gap-2">
                <span className="text-sky-400 mt-1 flex-shrink-0">›</span>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Auto-évaluation */}
        <div className="rounded-md border bg-card/40 p-3 space-y-3">
          <div className="flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-primary" />
            <h4 className="text-xs font-bold uppercase tracking-wide text-primary">
              Où en es-tu sur cette compétence ?
            </h4>
          </div>
          <div className="flex flex-wrap gap-2" data-testid={`livret-status-group-${sc.code}`}>
            {STATUS_ORDER.map((s) => (
              <StatusButton
                key={s}
                value={s}
                current={status}
                onClick={handleStatusClick}
                disabled={disabled}
              />
            ))}
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-muted-foreground">
                Tes notes personnelles (facultatif)
              </label>
              {savingNotes && (
                <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <Save className="w-3 h-3 animate-pulse" />
                  Enregistrement…
                </span>
              )}
            </div>
            <Textarea
              value={localNotes}
              onChange={handleNotesChange}
              placeholder="Ex. : je dois retravailler le freinage d'urgence à 50 km/h…"
              className="min-h-[60px] text-sm"
              data-testid={`livret-notes-${sc.code}`}
              disabled={disabled}
            />
            <p className="text-[10px] text-muted-foreground/80">
              Sauvegarde automatique · {localNotes.length}/2000
            </p>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

export default function MonLivretMoto() {
  useSeo({
    title: "Mon Livret d'apprentissage Moto — 38 compétences à valider",
    description: "Suis ta progression sur les 4 compétences officielles du permis A (REMC). 38 sous-compétences à valider : auto-évaluation, notes personnelles, progression sauvegardée.",
    path: '/mon-livret-moto',
  });

  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('C1');
  const [items, setItems] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // === IMPORT depuis lien enseignant (?import=<base64>) ===
  const importToken = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get('import');
  }, [location.search]);

  const importDecoded = useMemo(() => {
    if (!importToken) return null;
    const res = decodeImportPayload(importToken);
    if (!res.ok) {
      // Erreur de décodage → toast une fois après chargement
      return { error: res.error };
    }
    return { payload: res.payload };
  }, [importToken]);

  const [importDialogOpen, setImportDialogOpen] = useState(!!importToken);

  // Charge la progression au montage
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await api.getLivretProgression();
        if (!mounted) return;
        setItems(data.items || {});
        setSummary(data.summary || null);
      } catch (e) {
        if (!mounted) return;
        const msg = (e && e.message) || '';
        if (msg.includes('Not authenticated') || msg.includes('Invalid token') || msg.includes('User not found')) {
          setError('login_required');
        } else {
          setError(msg || 'Erreur de chargement');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Met à jour un item (status et/ou notes)
  const handleUpdate = useCallback(async (code, payload, opts = {}) => {
    // Optimistic update local
    setItems((prev) => ({
      ...prev,
      [code]: { ...(prev[code] || {}), ...payload },
    }));

    try {
      const res = await api.updateLivretItem(code, payload);
      setItems((prev) => ({ ...prev, [code]: res.item }));
      setSummary(res.summary);

      // Toast & badges seulement pour les changements de statut explicites
      if (!opts.silent && payload.status) {
        const meta = STATUS_META[payload.status];
        toast.success(`Compétence ${code} marquée « ${meta.label} »`);

        // Badge si la compétence parente vient d'être 100% acquise
        const parent = code.split('-')[0];
        const prevSummaryByComp = summary?.by_competence?.[parent];
        const newByComp = res.summary?.by_competence?.[parent];
        if (newByComp && newByComp.acquis >= newByComp.total &&
            (!prevSummaryByComp || prevSummaryByComp.acquis < prevSummaryByComp.total)) {
          setTimeout(() => {
            toast.success(`🏆 Compétence ${parent} maîtrisée à 100% !`, {
              duration: 5000,
            });
          }, 300);
        }
      }
      return res;
    } catch (e) {
      // Rollback en cas d'erreur (rechargement)
      try {
        const fresh = await api.getLivretProgression();
        setItems(fresh.items || {});
        setSummary(fresh.summary || null);
      } catch (_e) { /* ignore */ }
      if (!opts.silent) {
        toast.error(`Échec de la sauvegarde : ${e.message || 'erreur réseau'}`);
      }
      throw e;
    }
  }, [summary]);

  const handleReset = async () => {
    if (!window.confirm('Réinitialiser toute ta progression ? Cette action est irréversible.')) return;
    try {
      const res = await api.resetLivretProgression();
      setItems(res.items || {});
      setSummary(res.summary || null);
      toast.success('Progression réinitialisée.');
    } catch (e) {
      toast.error(`Échec : ${e.message || 'erreur'}`);
    }
  };

  // === Import des validations envoyées par l'enseignant ===
  const clearImportFromUrl = useCallback(() => {
    const sp = new URLSearchParams(location.search);
    sp.delete('import');
    const newSearch = sp.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}${location.hash || ''}`;
    navigate(newUrl, { replace: true });
  }, [location, navigate]);

  const handleImportApply = useCallback(async (entries) => {
    if (!entries || entries.length === 0) {
      setImportDialogOpen(false);
      return;
    }
    let ok = 0;
    let fail = 0;
    let lastSummary = null;
    let lastItems = { ...items };
    for (const [code, status] of entries) {
      try {
        const res = await api.updateLivretItem(code, { status });
        lastItems = { ...lastItems, [code]: res.item };
        lastSummary = res.summary;
        ok += 1;
      } catch (_e) {
        fail += 1;
      }
    }
    if (lastSummary) {
      setItems(lastItems);
      setSummary(lastSummary);
    }
    if (ok > 0 && fail === 0) {
      toast.success(`${ok} validation${ok > 1 ? 's' : ''} importée${ok > 1 ? 's' : ''} dans ton livret 🎉`);
    } else if (ok > 0 && fail > 0) {
      toast.warning(`${ok} importée${ok > 1 ? 's' : ''} · ${fail} échec${fail > 1 ? 's' : ''}`);
    } else {
      toast.error("Échec de l'import");
    }
    setImportDialogOpen(false);
    clearImportFromUrl();
  }, [items, clearImportFromUrl]);

  // Erreur de décodage du token → toast une fois et nettoyage URL
  useEffect(() => {
    if (importDecoded?.error) {
      toast.error(`Lien invalide : ${importDecoded.error}`);
      clearImportFromUrl();
    }
  }, [importDecoded, clearImportFromUrl]);

  const globalPercent = summary?.percent ?? 0;
  const totalAcquis = summary?.acquis ?? 0;
  const totalEnCours = summary?.en_cours ?? 0;
  const badges = summary?.badges ?? [];

  // === RENDER ===

  if (error === 'login_required') {
    const returnUrl = `${location.pathname}${location.search || ''}`;
    const loginHref = `/login?return=${encodeURIComponent(returnUrl)}`;
    const registerHref = `/register?return=${encodeURIComponent(returnUrl)}`;
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center space-y-4">
          <Lock className="w-12 h-12 mx-auto text-muted-foreground" />
          <h1 className="text-2xl font-bold">Connecte-toi pour accéder à ton livret</h1>
          <p className="text-muted-foreground">
            Ton livret d&apos;apprentissage est personnel. Connecte-toi pour suivre ta
            progression sur les 38 compétences officielles du permis A.
          </p>
          {importToken && (
            <p className="text-xs text-[#1d4ed8] font-bold">
              Tu as ouvert un lien d&apos;import de validations REMC : connecte-toi pour
              les appliquer dans ton livret.
            </p>
          )}
          <div className="flex flex-wrap gap-2 justify-center">
            <Button onClick={() => navigate(loginHref)} data-testid="livret-go-login">
              Se connecter
            </Button>
            <Button variant="outline" onClick={() => navigate(registerHref)} data-testid="livret-go-register">
              Créer un compte (gratuit)
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="mon-livret-moto-page">
      {/* Dialogue d'import depuis lien enseignant (?import=…) */}
      {importDecoded?.payload && (
        <LivretImportDialog
          payload={importDecoded.payload}
          open={importDialogOpen}
          onOpenChange={(o) => {
            setImportDialogOpen(o);
            if (!o) clearImportFromUrl();
          }}
          onApply={handleImportApply}
        />
      )}

      {/* HEADER */}
      <header className="border-b bg-gradient-to-b from-card to-background">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
          <Link
            to="/formation"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"
            data-testid="livret-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Retour Formation
          </Link>

          <div className="flex flex-wrap items-start gap-2 mb-3">
            <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
              REMC · Catégorie A
            </Badge>
            <Badge className="text-[10px] bg-green-600 text-white border-none">
              100 % gratuit
            </Badge>
          </div>

          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex-1 min-w-[280px]">
              <h1 className="font-serif text-2xl md:text-4xl font-bold tracking-tight mb-2 flex items-center gap-2">
                <BookOpen className="w-7 h-7 md:w-8 md:h-8 text-primary flex-shrink-0" />
                Mon Livret d&apos;Apprentissage Moto
              </h1>
              <p className="text-sm md:text-base text-muted-foreground max-w-3xl">
                Les 4 compétences officielles du permis A (REMC) — auto-évalue-toi sur les 38 sous-compétences à acquérir avant l&apos;examen. Ta progression est sauvegardée automatiquement.
              </p>
            </div>

            {/* Progression globale (desktop) */}
            <Card className="w-full md:w-auto md:min-w-[280px]" data-testid="livret-progress-card">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <div className="text-3xl font-bold tabular-nums">{totalAcquis}<span className="text-base font-normal text-muted-foreground">/38</span></div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">compétences acquises</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold tabular-nums" style={{ color: COMP_COLORS.C4 }}>
                      {Math.round(globalPercent)}%
                    </div>
                    <div className="text-[10px] text-muted-foreground">{totalEnCours} en cours</div>
                  </div>
                </div>
                <Progress value={globalPercent} className="h-2" />
                {badges.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {badges.map((b) => (
                      <Badge
                        key={b}
                        className="text-[10px] gap-1"
                        style={{ backgroundColor: COMP_COLORS[b], color: '#fff', border: 'none' }}
                        data-testid={`livret-badge-${b}`}
                      >
                        <Trophy className="w-3 h-3" /> {b} 100%
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 mt-6">
        {loading && (
          <div className="text-center py-12 text-muted-foreground" data-testid="livret-loading">
            Chargement de ta progression…
          </div>
        )}

        {!loading && error && error !== 'login_required' && (
          <Card className="border-destructive/40 bg-destructive/5">
            <CardContent className="p-6 text-center text-sm">
              <p className="text-destructive font-semibold mb-2">Erreur de chargement</p>
              <p className="text-muted-foreground mb-3">{error}</p>
              <Button onClick={() => window.location.reload()}>Réessayer</Button>
            </CardContent>
          </Card>
        )}

        {!loading && !error && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList
              className="grid grid-cols-2 sm:grid-cols-4 w-full h-auto gap-1"
              data-testid="livret-eleve-tabs"
            >
              {COMPETENCES_LIVRET.map((c) => {
                const Icon = COMP_ICONS[c.code] || BookOpen;
                const sub = summary?.by_competence?.[c.code] || { acquis: 0, total: c.sousCompetences.length };
                return (
                  <TabsTrigger
                    key={c.code}
                    value={c.code}
                    className="flex-col items-start gap-1.5 h-auto py-2.5 text-left whitespace-normal min-w-0 w-full"
                    data-testid={`livret-eleve-tab-${c.code}`}
                    style={activeTab === c.code ? { borderColor: COMP_COLORS[c.code] } : {}}
                  >
                    <div className="flex items-center gap-2 w-full min-w-0">
                      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: COMP_COLORS[c.code] }} />
                      <span className="font-bold" style={{ color: COMP_COLORS[c.code] }}>{c.code}</span>
                      <Badge variant="outline" className="ml-auto text-[10px] flex-shrink-0 tabular-nums">
                        {sub.acquis}/{sub.total}
                      </Badge>
                    </div>
                    <Progress
                      value={(sub.acquis / Math.max(sub.total, 1)) * 100}
                      className="h-1.5 w-full"
                    />
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {COMPETENCES_LIVRET.map((c) => (
              <TabsContent key={c.code} value={c.code} className="space-y-4">
                <Card style={{ borderTop: `3px solid ${COMP_COLORS[c.code]}` }}>
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <div
                        className="w-12 h-12 rounded-md flex items-center justify-center font-bold text-white text-sm flex-shrink-0"
                        style={{ backgroundColor: COMP_COLORS[c.code] }}
                      >
                        {c.code}
                      </div>
                      <div className="min-w-0">
                        <CardTitle className="text-base md:text-lg leading-snug">
                          {c.titre.replace(c.code + ' — ', '')}
                        </CardTitle>
                        <CardDescription className="mt-1 text-xs md:text-sm">
                          {c.contexte}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent>
                    <Accordion type="multiple" className="space-y-2">
                      {c.sousCompetences.map((sc) => (
                        <SubCompetencyCard
                          key={sc.code}
                          sc={sc}
                          item={items[sc.code]}
                          onUpdate={handleUpdate}
                        />
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        )}

        {/* Pied de page : actions secondaires */}
        {!loading && !error && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3 p-4 rounded-lg border bg-card/40">
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              Ton livret est privé · sauvegardé sur ton compte · accessible depuis tous tes appareils.
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              data-testid="livret-reset-btn"
              className="gap-1.5"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Réinitialiser ma progression
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
